<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import FunnelsAPI from 'dashboard/api/funnels';
import Button from 'dashboard/components-next/button/Button.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const { t } = useI18n();

const RANGE_DAYS = [7, 30, 90];
const DAY_IN_MS = 24 * 60 * 60 * 1000;
const DEFAULT_STEPS = [
  'flow.run_started',
  'flow.message_sent',
  'flow.run_completed',
];

const rangeDays = ref(30);
const steps = ref([...DEFAULT_STEPS]);
const result = ref(null);
const isFetching = ref(false);

const rangeOptions = computed(() =>
  RANGE_DAYS.map(days => ({
    value: days,
    label: t('FUNNELS.RANGE_DAYS', { count: days }),
  }))
);

const eventOptions = computed(() => [
  {
    value: 'conversation.created',
    label: t('FUNNELS.EVENTS.CONVERSATION_CREATED'),
  },
  {
    value: 'conversation.status_changed',
    label: t('FUNNELS.EVENTS.CONVERSATION_STATUS_CHANGED'),
  },
  {
    value: 'conversation.assignee_changed',
    label: t('FUNNELS.EVENTS.CONVERSATION_ASSIGNEE_CHANGED'),
  },
  { value: 'message.created', label: t('FUNNELS.EVENTS.MESSAGE_CREATED') },
  {
    value: 'message.status_changed',
    label: t('FUNNELS.EVENTS.MESSAGE_STATUS_CHANGED'),
  },
  { value: 'flow.run_started', label: t('FUNNELS.EVENTS.FLOW_RUN_STARTED') },
  { value: 'flow.message_sent', label: t('FUNNELS.EVENTS.FLOW_MESSAGE_SENT') },
  {
    value: 'flow.message_blocked',
    label: t('FUNNELS.EVENTS.FLOW_MESSAGE_BLOCKED'),
  },
  {
    value: 'flow.input_received',
    label: t('FUNNELS.EVENTS.FLOW_INPUT_RECEIVED'),
  },
  {
    value: 'flow.run_completed',
    label: t('FUNNELS.EVENTS.FLOW_RUN_COMPLETED'),
  },
  {
    value: 'flow.run_cancelled',
    label: t('FUNNELS.EVENTS.FLOW_RUN_CANCELLED'),
  },
  { value: 'flow.run_failed', label: t('FUNNELS.EVENTS.FLOW_RUN_FAILED') },
  {
    value: 'campaign.triggered',
    label: t('FUNNELS.EVENTS.CAMPAIGN_TRIGGERED'),
  },
  { value: 'csat.responded', label: t('FUNNELS.EVENTS.CSAT_RESPONDED') },
]);

const labelFor = eventName =>
  eventOptions.value.find(option => option.value === eventName)?.label ||
  eventName;

const updateStep = (index, value) => {
  steps.value = steps.value.map((step, stepIndex) =>
    stepIndex === index ? value : step
  );
};

const addStep = () => {
  steps.value = [...steps.value, 'message.created'];
};

const removeStep = index => {
  steps.value = steps.value.filter((_, stepIndex) => stepIndex !== index);
};

const runFunnel = async () => {
  if (steps.value.length < 2) {
    useAlert(t('FUNNELS.MIN_STEPS'));
    return;
  }

  isFetching.value = true;
  try {
    const to = new Date();
    const from = new Date(to.getTime() - rangeDays.value * DAY_IN_MS);
    const { data } = await FunnelsAPI.query({
      from: from.toISOString(),
      to: to.toISOString(),
      steps: steps.value.map(eventName => ({ event_name: eventName })),
    });
    result.value = data;
  } catch {
    useAlert(t('FUNNELS.ERROR'));
  } finally {
    isFetching.value = false;
  }
};

onMounted(runFunnel);
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-y-auto bg-n-background">
    <header class="px-6 pt-6 pb-5 border-b border-n-weak">
      <p
        class="text-xs font-medium uppercase tracking-[0.14em] text-n-slate-10"
      >
        {{ t('FUNNELS.EYEBROW') }}
      </p>
      <h1 class="mt-1 text-2xl font-semibold text-n-slate-12">
        {{ t('FUNNELS.TITLE') }}
      </h1>
      <p class="mt-2 max-w-3xl text-sm leading-6 text-n-slate-11">
        {{ t('FUNNELS.DESCRIPTION') }}
      </p>
    </header>

    <div class="grid gap-5 p-6 lg:grid-cols-[22rem_1fr]">
      <aside
        class="flex flex-col gap-4 p-4 border rounded-2xl border-n-weak bg-n-solid-1"
      >
        <label class="flex flex-col gap-1.5 text-sm text-n-slate-12">
          {{ t('FUNNELS.RANGE') }}
          <Select
            v-model="rangeDays"
            :options="rangeOptions"
            @update:model-value="runFunnel"
          />
        </label>

        <div class="flex flex-col gap-3">
          <p
            class="text-xs font-semibold uppercase tracking-[0.14em] text-n-slate-10"
          >
            {{ t('FUNNELS.STEPS_TITLE') }}
          </p>
          <div
            v-for="(step, index) in steps"
            :key="`${step}-${index}`"
            class="flex items-end gap-2"
          >
            <Select
              class="flex-1"
              :model-value="step"
              :options="eventOptions"
              @update:model-value="value => updateStep(index, value)"
            />
            <Button
              size="sm"
              color="slate"
              icon="i-lucide-trash-2"
              :aria-label="t('FUNNELS.REMOVE_STEP')"
              @click="removeStep(index)"
            />
          </div>
          <Button size="sm" color="slate" icon="i-lucide-plus" @click="addStep">
            {{ t('FUNNELS.ADD_STEP') }}
          </Button>
        </div>

        <Button :is-loading="isFetching" @click="runFunnel">
          {{ t('FUNNELS.RUN') }}
        </Button>
        <p class="text-xs leading-5 text-n-slate-10">
          {{ t('FUNNELS.STEPS_HELP') }}
        </p>
      </aside>

      <main class="flex flex-col gap-4 min-w-0">
        <div
          v-if="isFetching"
          class="flex items-center justify-center p-10 border rounded-2xl border-n-weak bg-n-solid-1"
        >
          <Spinner class="text-n-brand" />
        </div>

        <template v-else-if="result">
          <div class="p-5 border rounded-2xl border-n-weak bg-n-solid-1">
            <p class="text-xs uppercase tracking-[0.14em] text-n-slate-10">
              {{ t('FUNNELS.TOTAL') }}
            </p>
            <p class="mt-1 text-3xl font-semibold text-n-slate-12">
              {{ result.total }}
            </p>
          </div>

          <ol v-if="result.total" class="flex flex-col gap-3">
            <li
              v-for="(step, index) in result.steps"
              :key="`${step.event_name}-${index}`"
              class="p-5 border rounded-2xl border-n-weak bg-n-solid-1"
            >
              <div class="flex flex-wrap items-baseline justify-between gap-2">
                <p class="text-sm font-semibold text-n-slate-12">
                  {{ labelFor(step.event_name) }}
                </p>
                <p class="text-xl font-semibold text-n-slate-12">
                  {{ step.count }}
                </p>
              </div>
              <progress
                class="w-full h-1.5 mt-3 rounded accent-n-brand"
                :value="step.count"
                :max="result.total || 1"
              />
              <p class="mt-2 text-xs text-n-slate-10">
                {{
                  t('FUNNELS.CONVERSIONS', {
                    previous: step.conversion_from_previous,
                    first: step.conversion_from_first,
                  })
                }}
              </p>
            </li>
          </ol>

          <p
            v-else
            class="p-5 text-sm border rounded-2xl border-n-weak bg-n-solid-1 text-n-slate-11"
          >
            {{ t('FUNNELS.EMPTY') }}
          </p>
        </template>
      </main>
    </div>
  </section>
</template>
